import { useEffect, useMemo, useRef, useState } from 'react';
import { CustomDoseProduct } from './CustomDoseProduct.jsx';
import { api, networkWords } from '../api.js';
import { claimRequest, settleRequest, unsureSaveWords } from '../client-ref.js';
import { useConfig } from '../config.js';
import { effectiveParam, TANK_TO_DOSE_PARAM } from '../params.js';
import { track } from '../events.js';
import { getDisplayUnits, formatVolume } from '../units.js';
import {
  bottleRemaining,
  confirmedDoseMessage,
  doseFormValid,
  doseErrorMessage,
  doseInputsKey,
  doseRefusalMessage,
  initialCurrentField,
  parseMeasurement,
  sourceDoubtWarning,
} from '../dose-input.js';
import {
  doseFigureHeld,
  doubtAckLabel,
  doubtFromConfirmError,
  chosenProductLabel,
} from '../dose-display.js';

// Dose calculator — opens from a parameter card (Home → tap value →
// "Calculate dose" in ParamHistorySheet, or directly from a "Dose"
// chip on a param card).
//
// UX:
//   - Two big numbers at the top: current → target. User edits target.
//   - The recommended dose lands in a bold mL number with the product
//     name, citation, and explicit math underneath ("based on your
//     ~120g working water").
//   - Sensei's take below — short Sensei-voiced commentary on the
//     dose. Falls back silently if the spend guard blocks.
//   - Confirm-before-applied: nothing is logged until the user taps
//     Confirm. There's no auto-apply anywhere (anti-pattern #12).
//   - When the dose hits a daily cap, the calculator splits and
//     surfaces the multi-day plan instead of refusing.

const PARAM_LABEL = { alk: 'Alkalinity', ca: 'Calcium', mg: 'Magnesium' };
// Supply categories that can hold the bottle a dose draws from. Only
// volume units convert cleanly from a mL dose.
const DOSE_SUPPLY_CATEGORY = { alk: 'alkalinity', ca: 'calcium', mg: 'magnesium' };
const isVolumeUnit = (u) => /^(ml|l)$/i.test(String(u || '').trim());
const supplyMemoryKey = (tankId, apiParam) => `aqs.doseSupply.${tankId}.${apiParam}`;

// Long enough that a keystroke does not post, short enough to feel live.
const PREVIEW_DEBOUNCE_MS = 400;

// Callers pass the tank param key (alk/calcium/magnesium); the dose API
// speaks alk/ca/mg. Translate at the boundary so every component can
// keep handing us the key it already has from effectiveParams.
export function DoseCalculator({ tank, param, latestValue, onClose, onReload }) {
  const apiParam = TANK_TO_DOSE_PARAM[param] || param;
  const meta = effectiveParam(tank, param);
  // No reading means an EMPTY field, never the target. Putting the
  // target in a box labelled "current" shows the keeper a number they
  // never measured as though they had.
  const hasReading = latestValue !== null && latestValue !== undefined;
  const [current, setCurrent] = useState(initialCurrentField(latestValue));
  const [target, setTarget] = useState(meta?.target != null ? String(meta.target) : '');
  const [createRetest, setCreateRetest] = useState(true);
  // Acknowledgement that a doubted baseline is current. Deliberately
  // NOT sticky and reset whenever the preview changes: it is a
  // statement about one specific reading, not a preference.
  const [ackStale, setAckStale] = useState(false);
  const [preview, setPreview] = useState(null);
  const [productChoice, setProductChoice] = useState(meta?.product || null);
  // The product list folds to the chosen one: five chips first pushed the
  // dose below the fold on a phone.
  const [showProducts, setShowProducts] = useState(false);
  const doubtRef = useRef(null);
  // The keeper's own product form (feedback #60), and a nudge to re-ask
  // the preview after it is saved even when 'custom' was already chosen.
  const [showCustom, setShowCustom] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const hasCustom = !!tank.params?.[param]?.custom_product;
  const [customInvalid, setCustomInvalid] = useState(false);
  // Parameter settings, the custom product among them, are the owner's.
  const isOwner = tank.role === 'owner';
  const useDefaultProduct = async () => {
    // The owner clears the saved choice. An editor cannot, so they ask for
    // our default product by name, which the server honours over the saved
    // one (a missing product would fall back to the saved custom again).
    if (isOwner) {
      try {
        await api(`/api/tanks/${tank.id}/params`, {
          method: 'PATCH',
          body: { params: { [param]: { product: null } } },
        });
      } catch {
        // Falls through to asking for the default by name.
      }
    }
    setCustomInvalid(false);
    setErr('');
    setProductChoice(isOwner ? null : 'default');
    await onReload?.();
    setRefreshKey((k) => k + 1);
  };
  const [loading, setLoading] = useState(false);
  const [adviceLoading, setAdviceLoading] = useState(false);
  const [err, setErr] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [confirmedMsg, setConfirmedMsg] = useState('');
  const [confirmedSupply, setConfirmedSupply] = useState(null);

  // The bottle this dose draws from. Matching supplies (right category,
  // volume unit) are offered; the choice sticks per tank+param so the
  // next dose needs no thought. Empty string = don't track.
  const supplyCandidates = useMemo(
    () =>
      (tank?.supplies || []).filter(
        (s) => s.category === DOSE_SUPPLY_CATEGORY[apiParam] && isVolumeUnit(s.unit)
      ),
    [tank?.supplies, apiParam]
  );
  const [supplyId, setSupplyId] = useState(() => {
    try {
      const saved = localStorage.getItem(supplyMemoryKey(tank.id, apiParam));
      if (saved === '') return '';
      if (saved) return saved;
    } catch {
      /* private mode — fall through */
    }
    return null; // null = no saved preference; resolved against candidates below
  });
  // Only a CURRENT candidate may be posted — a remembered bottle that
  // was re-categorised or changed unit must not be drained invisibly
  // (review finding). Preference order: valid saved choice, then the
  // bottle the server already stamped (dose_param survives across
  // devices, unlike localStorage), then a sole candidate.
  const effectiveSupplyId = (() => {
    if (supplyId === '') return '';
    if (supplyId != null && supplyCandidates.some((s) => s.id === supplyId)) return supplyId;
    const stamped = supplyCandidates.find((s) => s.dose_param === param);
    if (stamped) return stamped.id;
    return supplyCandidates.length === 1 ? supplyCandidates[0].id : '';
  })();

  const usingLabel = tank?.volume_water_gal == null && tank?.volume_gallons != null;
  const volumeUnit = getDisplayUnits(tank).volume;

  const valid = useMemo(
    () => doseFormValid({ current, target, hasReading }),
    [current, target, hasReading]
  );

  // Server-reported doubt about the baseline this dose was scaled off.
  // The server re-checks and refuses at confirm regardless; this exists
  // so the keeper is asked BEFORE they tap, not rejected after.
  const doubtWarning = sourceDoubtWarning(
    preview?.source_doubt,
    (PARAM_LABEL[apiParam] || apiParam).toLowerCase()
  );
  const needsAck = !!preview?.source_doubt && !ackStale;
  const figureHeld = doseFigureHeld(preview, ackStale);
  const previewStale =
    !!preview && preview.for_key !== doseInputsKey({ current, target, product: productChoice });

  useEffect(() => {
    // Clear the stale figure the moment the inputs stop being valid, so
    // deleting the field to retype cannot leave a dose number on screen
    // that no longer corresponds to anything.
    if (!valid) {
      setPreview(null);
      setLoading(false);
      setAdviceLoading(false);
      if (!hasReading) setErr(doseErrorMessage('no_reading_logged'));
      return;
    }
    let cancelled = false;
    const forKey = doseInputsKey({ current, target, product: productChoice });
    setErr('');
    setAdviceLoading(false);
    // Debounced: the effect reruns on every keystroke, and posting each
    // intermediate value renders a dose figure for a number the keeper
    // is still in the middle of typing.
    const timer = setTimeout(() => {
      if (cancelled) return;
      setLoading(true);
      setAdviceLoading(false);
      const body = {
        param: apiParam,
        current: parseMeasurement(current),
        target: parseMeasurement(target),
        product: productChoice || undefined,
      };
      // The math first, so the mL figure and its warnings never wait on the
      // AI; Sensei's take follows and is dropped if the numbers moved.
      api(`/api/tanks/${tank.id}/dose/preview`, {
        method: 'POST',
        body: { ...body, advice: false },
      })
        .then((p) => {
          if (cancelled) return;
          setPreview({ ...p, advice: null, for_key: forKey });
          setCustomInvalid(false);
          setAckStale(false);
          setAdviceLoading(true);
          api(`/api/tanks/${tank.id}/dose/preview`, { method: 'POST', body })
            .then((full) => {
              if (cancelled) return;
              const same =
                full.amount_ml === p.amount_ml &&
                full.product_key === p.product_key &&
                (full.warnings || []).join('|') === (p.warnings || []).join('|') &&
                (full.source_doubt?.reason || '') === (p.source_doubt?.reason || '');
              if (!same) return;
              setPreview((cur) => (cur ? { ...cur, advice: full.advice || null } : cur));
            })
            .catch(() => {})
            .finally(() => {
              if (!cancelled) setAdviceLoading(false);
            });
        })
        .catch((e) => {
          if (cancelled) return;
          setPreview(null);
          // api() puts the server's machine-readable code on both
          // err.payload.error and err.message.
          const code = e.payload?.error || e.message;
          setCustomInvalid(code === 'custom_product_invalid');
          // A lost signal and a refused role are not "check your entries".
          setErr(
            networkWords(e) ||
              (e.status === 403 && e.payload?.error) ||
              doseRefusalMessage(e.payload?.error ? e.payload : { error: code })
          );
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    }, PREVIEW_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [tank.id, apiParam, current, target, valid, hasReading, productChoice, refreshKey]);

  const handleConfirm = async () => {
    if (!preview?.amount_ml || preview.amount_ml <= 0 || previewStale) return;
    setConfirming(true);
    setErr('');
    const body = {
      param: apiParam,
      amount_ml: preview.amount_ml,
      product: preview.product?.key,
      product_label: preview.product?.label,
      custom_strength: preview.custom_strength ?? undefined,
      target_value: parseMeasurement(target),
      current_value: parseMeasurement(current),
      create_retest_task: createRetest,
      acknowledged_stale_source: ackStale,
      supply_id: effectiveSupplyId || undefined,
    };
    // One "I dosed it" keeps its id until the server answers it, so a retry
    // after a lost reply, a 5xx or a closed sheet logs it once (nightly
    // sweep 2, 2026-10-24).
    const scope = `dose:${tank.id}:${apiParam}`;
    const ref = claimRequest(
      scope,
      JSON.stringify([apiParam, body.amount_ml, body.product, body.supply_id || null])
    );
    let result;
    try {
      result = await api(`/api/tanks/${tank.id}/dose/confirm`, {
        method: 'POST',
        body: { ...body, client_ref: ref },
      });
    } catch (e) {
      // The id already holds another dose: the next tap is a new save.
      if (e.payload?.error === 'client_ref_mismatch') settleRequest(scope);
      // A doubt the preview did not have: ask it now, beside the dose, so
      // the keeper can answer and log a dose already given.
      const lateDoubt = doubtFromConfirmError(e);
      if (lateDoubt) {
        setPreview((cur) => (cur ? { ...cur, source_doubt: lateDoubt } : cur));
        setAckStale(false);
        // The question sits above the dose; the keeper's thumb is on the
        // button at the bottom.
        setTimeout(() => doubtRef.current?.scrollIntoView?.({ block: 'center' }), 0);
      }
      // The server answers with a code (stale_source_unconfirmed); show the
      // words for it, never the code itself.
      setErr(
        unsureSaveWords(e) ||
          doseRefusalMessage(
            e.payload?.error ? e.payload : { error: e.message },
            'Could not log the dose.'
          )
      );
      setConfirming(false);
      return;
    }
    settleRequest(scope);
    setConfirmedSupply(result?.supply || null);
    try {
      localStorage.setItem(supplyMemoryKey(tank.id, apiParam), effectiveSupplyId);
    } catch {
      /* private mode — non-fatal */
    }
    // Remember the chosen product on this param so the next dose (and
    // Sensei's alternatives copy) defaults to it. Best-effort — a
    // failure here never blocks logging the dose itself.
    const chosen = preview.product?.key || null;
    if (chosen && chosen !== meta?.product) {
      api(`/api/tanks/${tank.id}/params`, {
        method: 'PATCH',
        body: { params: { [param]: { product: chosen } } },
      }).catch(() => {});
    }
    setConfirmedMsg(
      confirmedDoseMessage({
        event: result?.event,
        amountMl: result?.event?.duplicate ? Number(result.event.amount_ml) : preview.amount_ml,
        label: preview.product?.label || meta?.label || apiParam,
        retestTask: result?.retest_task,
      })
    );
    track('dose.confirm', 'dose', { param: apiParam, amount_ml: preview.amount_ml });
    setConfirming(false);
    // Logged. A refresh that fails is not a failed dose (sweep 11 did the
    // same for the dose log and the water change).
    await Promise.resolve(onReload?.()).catch(() => {});
  };

  const blocked = preview?.amount_ml === 0;
  const advice = preview?.advice;

  return (
    <div className="dc-overlay" role="dialog" aria-modal="true" aria-labelledby="dc-title">
      <div className="dc-sheet" onClick={(e) => e.stopPropagation()}>
        <header className="dc-header">
          <div>
            <div className="dc-eyebrow">Dose calculator</div>
            <h2 id="dc-title" className="dc-title font-heading">
              {(meta?.label || PARAM_LABEL[param] || param).toUpperCase()}
            </h2>
          </div>
          <button type="button" aria-label="Close" className="dc-close" onClick={onClose}>
            ×
          </button>
        </header>

        {confirmedMsg && (
          <div className="dc-confirmed">
            <div className="dc-confirmed-icon" aria-hidden="true">
              ✓
            </div>
            <div>
              {confirmedMsg}
              {confirmedSupply && confirmedSupply.remaining != null && (
                <div className="dc-confirmed-supply font-mono">
                  {confirmedSupply.name}: {confirmedSupply.remaining} {confirmedSupply.unit} left
                </div>
              )}
            </div>
            <button type="button" className="dc-confirmed-close" onClick={onClose}>
              Done
            </button>
          </div>
        )}
        {confirmedMsg && supplyCandidates.length === 0 && !confirmedSupply && (
          <TrackBottleNudge tank={tank} apiParam={apiParam} preview={preview} onReload={onReload} />
        )}

        {!confirmedMsg && (
          <>
            <div className="dc-inputs">
              <label className="dc-input">
                <span className="dc-input-label">Current</span>
                <div className="dc-input-row">
                  <input
                    type="number"
                    inputMode="decimal"
                    step="any"
                    value={current}
                    onChange={(e) => setCurrent(e.target.value)}
                    autoFocus
                  />
                  <span className="dc-unit">{meta?.unit || preview?.product?.unit || ''}</span>
                </div>
              </label>
              <div className="dc-arrow" aria-hidden="true">
                →
              </div>
              <label className="dc-input">
                <span className="dc-input-label">Target</span>
                <div className="dc-input-row">
                  <input
                    type="number"
                    inputMode="decimal"
                    step="any"
                    value={target}
                    onChange={(e) => setTarget(e.target.value)}
                  />
                  <span className="dc-unit">{meta?.unit || preview?.product?.unit || ''}</span>
                </div>
              </label>
            </div>

            {!valid && <p className="dc-hint">Enter both current and target to see a dose.</p>}

            {preview?.products?.length > 0 && !showProducts && (
              <div className="dc-products dc-products-folded">
                <div className="dc-products-label">Dosing with</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span className="dc-products-current" style={{ flex: 1, minWidth: 0 }}>
                    {chosenProductLabel(preview, productChoice)}
                  </span>
                  <button
                    type="button"
                    className="dc-product-chip dc-product-change"
                    style={{ minHeight: 44, justifyContent: 'center' }}
                    aria-expanded={false}
                    onClick={() => setShowProducts(true)}
                  >
                    <span>Change product</span>
                  </button>
                </div>
                {/* Not behind Change product too (owner, 2026-10-23: three
                    taps at most from Home, and this took four). */}
                {isOwner && !showCustom && (
                  <button
                    type="button"
                    className="dc-own-product"
                    onClick={() => setShowCustom(true)}
                  >
                    {hasCustom ? 'Edit my own product' : 'Not listed? Use my own product'}
                  </button>
                )}
              </div>
            )}
            {preview?.products?.length > 0 && showProducts && (
              <div className="dc-products">
                <div className="dc-products-label">Dosing with</div>
                <div className="dc-products-chips">
                  {preview.products.map((pr) => {
                    const active = (productChoice || preview.product_key) === pr.key;
                    return (
                      <button
                        type="button"
                        key={pr.key}
                        className={`dc-product-chip ${active ? 'dc-product-chip-on' : ''}`}
                        aria-pressed={active}
                        style={{ minHeight: 44 }}
                        onClick={() => {
                          setProductChoice(pr.key);
                          setShowProducts(false);
                        }}
                      >
                        <span>{pr.label}</span>
                        {pr.multi && <span className="dc-product-multi">raises more than one</span>}
                        {pr.custom && <span className="dc-product-multi">from your label</span>}
                      </button>
                    );
                  })}
                  {isOwner && (
                    <button
                      type="button"
                      className="dc-product-chip"
                      aria-expanded={showCustom}
                      style={{ minHeight: 44 }}
                      onClick={() => setShowCustom((v) => !v)}
                    >
                      <span>{hasCustom ? 'Edit my product' : '+ My own product'}</span>
                    </button>
                  )}
                </div>
                {!isOwner && (
                  <p className="dc-hint" style={{ marginTop: 6 }}>
                    Using a product that is not listed? The tank owner can add it from its label.
                  </p>
                )}
              </div>
            )}
            {customInvalid && !preview && (
              <div className="dc-products" style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {isOwner && (
                  <button
                    type="button"
                    className="dc-product-chip"
                    onClick={() => setShowCustom(true)}
                  >
                    <span>Edit my product</span>
                  </button>
                )}
                <button type="button" className="dc-product-chip" onClick={useDefaultProduct}>
                  <span>Use one of ours instead</span>
                </button>
              </div>
            )}
            {showCustom && (
              <CustomDoseProduct
                tank={tank}
                tankParam={param}
                apiParam={apiParam}
                onCancel={() => setShowCustom(false)}
                onSaved={async ({ removed } = {}) => {
                  setShowCustom(false);
                  setShowProducts(false);
                  setCustomInvalid(false);
                  setErr('');
                  await onReload?.();
                  setProductChoice(removed ? null : 'custom');
                  setRefreshKey((k) => k + 1);
                }}
              />
            )}

            {loading && (
              <div className="dc-loading">
                <div className="dc-skel dc-skel-big" />
                <div className="dc-skel dc-skel-line" />
              </div>
            )}

            {err && (
              <div className="dc-err" role="alert">
                {err}
              </div>
            )}

            {preview && !loading && (
              <>
                {doubtWarning && !blocked && !preview.already_at_or_above && (
                  <div className="dc-doubt" role="note" ref={doubtRef}>
                    <p className="dc-doubt-text">{doubtWarning}</p>
                    <label className="dc-doubt-ack">
                      <input
                        type="checkbox"
                        checked={ackStale}
                        onChange={(e) => setAckStale(e.target.checked)}
                      />
                      <span>{doubtAckLabel(preview?.source_doubt)}</span>
                    </label>
                  </div>
                )}
                <div
                  className={`dc-result ${blocked ? 'dc-result-blocked' : ''}`}
                  style={previewStale ? { opacity: 0.45 } : undefined}
                  aria-busy={previewStale || undefined}
                >
                  {preview.already_at_or_above ? (
                    <div className="dc-result-flat">
                      <div className="dc-result-flat-icon">✓</div>
                      <div>{preview.warnings?.[0]}</div>
                    </div>
                  ) : (
                    <>
                      <div className="dc-result-label">
                        {blocked
                          ? 'No dose right now'
                          : figureHeld
                            ? 'Check the reading first'
                            : 'Recommended dose'}
                      </div>
                      {figureHeld ? (
                        <p className="dc-result-held" style={{ margin: '6px 0 0', fontSize: 16 }}>
                          Tick the box above once you have checked it, and the dose shows here.
                        </p>
                      ) : (
                        <div className="dc-result-value">
                          {preview.amount_ml}
                          <span className="dc-result-unit">mL</span>
                        </div>
                      )}
                      {preview.product && (
                        <div className="dc-result-product">{preview.product.label}</div>
                      )}
                      {preview.product?.recipe && (
                        <div className="dc-result-recipe">{preview.product.recipe}</div>
                      )}
                      <div className="dc-result-math">
                        {preview.math?.gap != null && (
                          <>
                            Gap {preview.math.gap}
                            {preview.product?.unit ? ` ${preview.product.unit}` : ''} ·{' '}
                          </>
                        )}
                        based on ~{formatVolume(preview.working_volume_gal, volumeUnit)} of water
                        {usingLabel && (
                          <span className="dc-result-warn">
                            {' '}
                            (using label volume — set up Tank build for precision)
                          </span>
                        )}
                      </div>
                      {preview.days_to_target > 1 && (
                        <div className="dc-result-split">
                          Split across {preview.days_to_target} days. Today's portion is{' '}
                          {preview.today_gap} {preview.product?.unit}.
                        </div>
                      )}
                    </>
                  )}
                </div>

                {preview.dosed_since_test == null && preview.already_dosed_24h_ml > 0 && (
                  <p className="dc-hint">
                    Already dosed in the last 24 hours: {preview.already_dosed_24h_ml} mL.
                    Today&rsquo;s number allows for it.
                  </p>
                )}

                {preview.maintenance && !figureHeld && (
                  <div className="dc-maintenance">
                    <div className="dc-maintenance-label">To hold target</div>
                    {preview.maintenance.verify_consumption ||
                    preview.maintenance.ml_per_day == null ? (
                      <p className="dc-maintenance-text">
                        Your tests read this{' '}
                        {(meta?.label || PARAM_LABEL[param] || param).toLowerCase()} as dropping
                        about {preview.maintenance.consumption_per_day} {preview.maintenance.unit} a
                        day. That is faster than the largest daily rise the calculator plans, and a
                        misread test is the most likely reason, so no daily dose is suggested. Keep
                        testing over the next few days; if the drop holds, set a daily dose from
                        your product&rsquo;s label.
                      </p>
                    ) : (
                      <p className="dc-maintenance-text">
                        Your tests over the last {preview.maintenance.span_days || 'few'} days read
                        this {(meta?.label || PARAM_LABEL[param] || param).toLowerCase()} as
                        dropping about {preview.maintenance.consumption_per_day}{' '}
                        {preview.maintenance.unit} a day
                        {preview.maintenance.on_top_of_dosing ? ', with your dosing in place' : ''}.
                        A daily dose of about{' '}
                        <strong>
                          {preview.maintenance.ml_per_day} mL of {preview.maintenance.product.label}
                        </strong>{' '}
                        {preview.maintenance.on_top_of_dosing
                          ? 'on top of what you already dose'
                          : ''}{' '}
                        would offset that so you stop chasing the same correction.
                        {preview.maintenance.capped && " Held to the label's daily maximum."}
                        {preview.maintenance.multi &&
                          ' Note: this product moves more than one parameter, so it lifts the others too.'}
                      </p>
                    )}
                  </div>
                )}

                {preview.warnings?.length > 0 && !preview.already_at_or_above && (
                  <ul className="dc-warnings">
                    {preview.warnings.map((w, i) => (
                      <li key={i}>{w}</li>
                    ))}
                  </ul>
                )}

                <div className="dc-sensei">
                  <div className="dc-sensei-eyebrow">SENSEI'S TAKE</div>
                  {figureHeld ? (
                    <p className="dc-sensei-text dc-sensei-fallback">
                      Sensei&rsquo;s take shows with the dose.
                    </p>
                  ) : advice ? (
                    <p className="dc-sensei-text">{advice.advice}</p>
                  ) : adviceLoading ? (
                    <p className="dc-sensei-text dc-sensei-fallback">
                      Sensei is reading your numbers…
                    </p>
                  ) : (
                    <p className="dc-sensei-text dc-sensei-fallback">
                      Sensei is taking a short break. The math above stands on its own.
                    </p>
                  )}
                </div>

                {preview.product?.citation && (
                  <p className="dc-citation">Citation: {preview.product.citation}</p>
                )}

                {!blocked && !preview.already_at_or_above && (
                  <>
                    {supplyCandidates.length > 0 && (
                      <label className="dc-supply-row">
                        <span className="dc-supply-label">Draws from</span>
                        <select
                          value={effectiveSupplyId}
                          onChange={(e) => setSupplyId(e.target.value)}
                        >
                          {supplyCandidates.map((s) => (
                            <option key={s.id} value={s.id}>
                              {s.name}
                              {s.remaining != null ? ` (${s.remaining} ${s.unit} left)` : ''}
                            </option>
                          ))}
                          <option value="">Don&rsquo;t track</option>
                        </select>
                      </label>
                    )}
                    <label className="dc-retest">
                      <input
                        type="checkbox"
                        checked={createRetest}
                        onChange={(e) => setCreateRetest(e.target.checked)}
                      />
                      <span>Schedule a retest in 36 hours</span>
                    </label>

                    <div className="dc-actions">
                      <button
                        type="button"
                        className="dc-cancel"
                        onClick={onClose}
                        disabled={confirming}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="dc-confirm"
                        onClick={handleConfirm}
                        disabled={confirming || needsAck || previewStale}
                        title={needsAck ? 'Confirm the reading is current first' : undefined}
                      >
                        {confirming
                          ? 'Logging…'
                          : previewStale
                            ? 'Updating the dose…'
                            : figureHeld
                              ? 'Check the reading first'
                              : `I dosed ${preview.amount_ml} mL`}
                      </button>
                    </div>
                    <p className="dc-confirm-hint">
                      Sensei does not auto-dose. Add the liquid yourself, then tap &ldquo;I
                      dosed&rdquo; so the next reading is read against this dose.
                    </p>
                  </>
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// The moment-of-use supply capture: the user just logged a dose from a
// bottle the app doesn't know about. One question ("how big is it?"),
// everything else prefilled from the dose itself — name from the
// product, unit mL, daily use from the maintenance suggestion when the
// param is falling. Dismissing it costs nothing and it never blocks
// the confirmed state.
function TrackBottleNudge({ tank, apiParam, preview, onReload }) {
  const productLabel = preview?.product?.label || PARAM_LABEL[apiParam] || 'this product';
  // Zero-entry prefill: the presets already know the retail package
  // for most dosing products ("ESV B-Ionic Alkalinity 1 gal" = 3785
  // mL). Loose two-way label match, volume units only — a dry 5 lb
  // bag can't drain by mL, so it stays blank rather than wrong.
  const presets =
    useConfig()?.supply_presets?.categories?.[DOSE_SUPPLY_CATEGORY[apiParam]]?.presets || [];
  const norm = (s) => String(s || '').toLowerCase();
  const presetMatch = presets.find((p) => {
    if (!isVolumeUnit(p.unit) || !(Number(p.size) > 0)) return false;
    return norm(p.label).includes(norm(productLabel)) || norm(productLabel).includes(norm(p.label));
  });
  const presetSizeMl = presetMatch
    ? String(presetMatch.unit).trim().toLowerCase() === 'l'
      ? Number(presetMatch.size) * 1000
      : Number(presetMatch.size)
    : null;
  const [size, setSize] = useState(presetSizeMl ? String(presetSizeMl) : '');
  const [left, setLeft] = useState('');
  const [leftTouched, setLeftTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [err, setErr] = useState('');

  if (dismissed) return null;
  if (done) {
    return (
      <div className="dc-nudge">
        <p className="dc-nudge-done">
          Tracking it. Every dose you log draws the bottle down, and Sensei warns before it runs
          out.
        </p>
      </div>
    );
  }

  const effectiveLeft = leftTouched ? left : size;

  const save = async () => {
    const sizeN = Number(size);
    const remaining = bottleRemaining(size, effectiveLeft);
    if (!(sizeN > 0) || remaining === null || busy) return;
    setBusy(true);
    setErr('');
    try {
      const s = await api(`/api/tanks/${tank.id}/supplies`, {
        method: 'POST',
        body: {
          name: productLabel.slice(0, 100),
          category: DOSE_SUPPLY_CATEGORY[apiParam] || 'other',
          unit: 'mL',
          size: sizeN,
          remaining,
          daily_use: preview?.maintenance?.ml_per_day || 0,
        },
      });
      try {
        localStorage.setItem(supplyMemoryKey(tank.id, apiParam), s.id);
      } catch {
        /* private mode — non-fatal */
      }
      setDone(true);
      await onReload?.();
    } catch (e) {
      setErr(e.message || 'Could not add the supply.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="dc-nudge">
      <div className="dc-nudge-eyebrow">Track this bottle?</div>
      <p className="dc-nudge-text">
        Tell Sensei how big the {productLabel} bottle is and every logged dose draws it down — you
        get a warning before it runs out, not after.
      </p>
      <div className="dc-nudge-fields">
        <label>
          <span>Bottle size (mL)</span>
          <input
            type="number"
            inputMode="decimal"
            placeholder="2000"
            value={size}
            onChange={(e) => setSize(e.target.value)}
          />
        </label>
        <label>
          <span>Left now (mL)</span>
          <input
            type="number"
            inputMode="decimal"
            placeholder={size || 'same'}
            value={effectiveLeft}
            onChange={(e) => {
              setLeftTouched(true);
              setLeft(e.target.value);
            }}
          />
        </label>
      </div>
      {err && (
        <p className="dc-nudge-err" role="alert">
          {err}
        </p>
      )}
      <div className="dc-nudge-actions">
        <button
          type="button"
          className="dc-nudge-skip"
          onClick={() => setDismissed(true)}
          disabled={busy}
        >
          Not now
        </button>
        <button
          type="button"
          className="dc-nudge-save font-heading"
          onClick={save}
          disabled={busy || !(Number(size) > 0)}
        >
          {busy ? 'Adding…' : 'Track it'}
        </button>
      </div>
    </div>
  );
}
